import React from 'react'
import { Typography, makeStyles } from '@material-ui/core'
import StarIcon from '@material-ui/icons/Star'
import StarBorderIcon from '@material-ui/icons/StarBorder'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import Zoom from '@mui/material/Zoom'
import { useIntl } from 'react-intl'

import DefaultCourseGroupImage from '../../../assets/images/courseGroup/FallbackCourseGroupImage.jpeg'
import { CapeeshColors } from '../../../assets/ColorPalette'
import type {
    CourseLessonOrderItemDto,
    CourseLessonOrderItemProgressionDto,
} from '../../../Logic/StudentCourseDtos'
import { useStudentImage } from '../useStudentImage'
import { studentScreenMessages } from '../../../Views/StudentPrivate/StudentScreen.i18n.Messages'

// ─── Styles ───────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
    cardRoot: {
        display: 'flex',
        flexDirection: 'row' as const,
        alignItems: 'center',
        padding: 12,
        borderRadius: 14,
        backgroundColor: '#FFFFFF',
        boxShadow: '0px 1px 6px rgba(0, 0, 0, 0.06)',
        cursor: 'pointer',
        transition: 'box-shadow 200ms ease, transform 150ms ease',
        '&:hover': {
            boxShadow: '0px 4px 14px rgba(0, 0, 0, 0.10)',
            transform: 'translateY(-1px)',
        },
        gap: 12,
        overflow: 'hidden',
        '@media (max-width: 599px)': {
            padding: 10,
            gap: 10,
            borderRadius: 12,
        },
    },
    imageContainer: {
        width: 72,
        height: 72,
        minWidth: 72,
        borderRadius: 10,
        overflow: 'hidden',
        backgroundColor: CapeeshColors.Gray5,
        '@media (max-width: 599px)': {
            width: 56,
            height: 56,
            minWidth: 56,
            borderRadius: 8,
        },
    },
    lessonImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover' as const,
    },
    textBlock: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column' as const,
        justifyContent: 'center',
        minWidth: 0,
        gap: 2,
    },
    lessonName: {
        fontFamily: 'Rubik',
        fontSize: 14,
        fontWeight: 600,
        lineHeight: '140%',
        color: CapeeshColors.DarkText,
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        '@media (max-width: 599px)': {
            whiteSpace: 'normal' as const,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical' as const,
            fontSize: 13,
        },
    },
    lessonDescription: {
        fontFamily: 'Rubik',
        fontSize: 12,
        fontWeight: 400,
        lineHeight: '150%',
        color: CapeeshColors.Gray1,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical' as const,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    lessonType: {
        fontFamily: 'Rubik',
        fontSize: 11,
        fontWeight: 500,
        color: CapeeshColors.Primary600,
        marginTop: 2,
    },
    rightSection: {
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        minWidth: 40,
    },
    starsRow: {
        display: 'flex',
        flexDirection: 'row' as const,
        gap: 1,
    },
    starFilled: {
        fontSize: 16,
        color: CapeeshColors.YellowBright,
    },
    starEmpty: {
        fontSize: 16,
        color: CapeeshColors.Gray4,
    },
    passedIcon: {
        fontSize: 20,
        color: CapeeshColors.GreenBright,
        marginTop: 2,
    },
}))

// ─── Types ────────────────────────────────────────────────────────

interface LessonCardProps {
    lesson: CourseLessonOrderItemDto
    progression: CourseLessonOrderItemProgressionDto | null
    onSelect?: (lesson: CourseLessonOrderItemDto) => void
    index: number
}

// ─── Component ────────────────────────────────────────────────────

const LessonCard: React.FC<LessonCardProps> = ({
    lesson,
    progression,
    onSelect,
    index,
}) => {
    const classes = useStyles()
    const intl = useIntl()
    const imageBlobUrl = useStudentImage(lesson?.imageUrl)

    if (!lesson) return null

    const name = lesson.name || intl.formatMessage(studentScreenMessages.lessonFallback)
    const description = lesson.description || ''
    const stars = progression?.stars ?? 0
    const maxStars = 3 // lessons have max 3 stars
    const passed = progression?.passed ?? false

    const handleClick = () => {
        if (onSelect) {
            onSelect(lesson)
        }
    }

    return (
        <Zoom in={true} style={{ transitionDelay: `${(index || 0) * 40}ms` }}>
            <div className={classes.cardRoot} onClick={handleClick}>
                {/* Lesson image */}
                <div className={classes.imageContainer}>
                    <img
                        src={imageBlobUrl || DefaultCourseGroupImage}
                        className={classes.lessonImage}
                        alt={name}
                    />
                </div>

                {/* Name + description */}
                <div className={classes.textBlock}>
                    <Typography className={classes.lessonName}>
                        {name}
                    </Typography>
                    {description && (
                        <Typography className={classes.lessonDescription}>
                            {description}
                        </Typography>
                    )}
                    {lesson.topicLessonTypeName && (
                        <Typography className={classes.lessonType}>
                            {lesson.topicLessonTypeName}
                        </Typography>
                    )}
                </div>

                {/* Stars + passed indicator */}
                <div className={classes.rightSection}>
                    <div className={classes.starsRow}>
                        {Array.from({ length: maxStars }).map((_, i) =>
                            i < stars ? (
                                <StarIcon key={i} className={classes.starFilled} />
                            ) : (
                                <StarBorderIcon key={i} className={classes.starEmpty} />
                            )
                        )}
                    </div>
                    {passed && <CheckCircleIcon className={classes.passedIcon} />}
                </div>
            </div>
        </Zoom>
    )
}

export default LessonCard
